<template>
  <div v-if="!isChannelLoading">
    <div class="page page-highlighter" v-if="!hasChannel">
      <div class="page-header">
        <div class="container">
          <h2 class="page-title">{{$t('channel.creator.pageTitle')}}</h2>
          <h5 class="page-subtitle">{{$t('channel.creator.pageSubTitle')}}</h5>
        </div>
      </div>
      <div class="container">
        <div class="row">
          <div class="col-md-8">
            <div class="general-information-section">
              <!-- <h5 class="section-title">ข้อมูลทั่วไป</h5> -->
              <div class="form-group">
                <label class="form-title">{{$t('channel.creator.name')}}</label>
                <input
                  class="form-control"
                  :class="{ 'is-invalid': error.name }"
                  @blur="validateRequiredField('name')"
                  v-model="channel.name"/>
                <div class="invalid-feedback">{{error.name}}</div>
              </div>
              <div class="form-group">
                <label class="form-title">{{$t('channel.creator.sourceRetention')}}</label>
                <input
                  class="form-control short-form"
                  type="number"
                  :class="{ 'is-invalid': error.archiveAgeInMinutes }"
                  @blur="validateRequiredField('archiveAgeInMinutes')"
                  v-model="channel.archiveAgeInMinutes"/>
                  <div class="invalid-feedback">{{error.archiveAgeInMinutes}}</div>
              </div>
            </div>
            <div class="general-information-section">
              <SectionManageChannel
                :streams="channel.streams"
                :onUpdateStream="onUpdateStream"
                :onDeleteStream="onDeleteStream"/>
            </div>
            <div class="general-information-section optional-section">
              <h5 class="section-title">
                <span>{{$t('channel.creator.authentication')}}</span>
                <i
                  class="fa fa-chevron-down chevron-icon"
                  :class="{ rotate: showOptionalSection}"
                  @click="onToggleOptionalSection"/>
              </h5>
              <div class="optional-element-section" v-if="showOptionalSection">
                <label class="form-title">Headers</label>
                <div class="optional-element" v-for="(header, index) in channel.headers" :key="index">
                  <input class="form-control short-form" placeholder="Key" v-model="channel.headers[index].name"/>
                  <input class="form-control" placeholder="Value" v-model="channel.headers[index].value"/>
                  <div class="close-button-container" type="button" @click="onClickRemoveHeader(index)">
                    <div>
                      <i class="fa fa-times" />
                    </div>
                  </div>
                </div>
                <md-button
                  class="md-raised"
                  type="button"
                  @click="onClickAddHeaderButton">
                  + {{$t('common:add')}} Headers
                </md-button>
              </div>
              <div class="optional-element-section" v-if="showOptionalSection">
                <label class="form-title">uriRewriteRules</label>
                <div class="optional-element" v-for="(uri, index) in channel.uriRules" :key="index">
                  <input class="form-control short-form" placeholder="Search" v-model="channel.uriRules[index].search"/>
                  <input class="form-control" placeholder="Replace" v-model="channel.uriRules[index].replace"/>
                  <div class="close-button-container" type="button" @click="onClickRemoveUriRule(index)">
                    <div>
                      <i class="fa fa-times" />
                    </div>
                  </div>
                </div>
                <md-button
                  v-if="showOptionalSection"
                  class="md-raised"
                  @click="onClickAddUriRule">
                  + {{$t('common:add')}} uriRewriteRules
                </md-button>
              </div>
            </div>
            <div class="save-button-container">
              <div class="text-danger" v-if="error.system">{{error.system}}</div>
              <md-button
                class="md-raised md-primary"
                :disabled="!canClickSave"
                @click="onCreateProject">
                {{$t('common:save')}}
              </md-button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="empty-container" v-if="hasChannel">
      <i class="fa fa-video-camera icon"></i>
      <h3>{{$t('channel.creator.alreadyHasChannel.title')}}</h3>
      <h5 class="text-secondary">{{$t('channel.creator.alreadyHasChannel.subtitle')}}</h5>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { createChannel } from '@/api/TimeMachine';
import SectionManageChannel from './parts/channel/SectionManageChannel.vue';

export default {
  components: {
    SectionManageChannel,
  },
  data() {
    return {
      channel: {
        name: '',
        archiveAgeInMinutes: 7,
        streams: [],
        headers: [
          {
            name: '',
            value: '',
          },
        ],
        uriRules: [
          {
            name: '',
            value: '',
          },
        ],
      },
      showOptionalSection: false,
      error: {},
    };
  },
  created() {
    this.$store.dispatch('loadChannels');
  },
  computed: {
    ...mapState({
      channels: state => state.teamInformation.channels,
      isChannelLoading: state => state.teamInformation.loadingState.isLoading,
    }),
    teamName() {
      return this.$route.params.teamName;
    },
    canClickSave() {
      return this.channel.name && this.channel.archiveAgeInMinutes && this.channel.streams.length > 0;
    },
    hasChannel() {
      return this.channels && this.channels.length !== 0;
    },
  },
  methods: {
    onClickAddHeaderButton() {
      this.channel.headers.push({ name: '', value: '' });
    },
    onClickRemoveHeader(index) {
      this.channel.headers.splice(index, 1);
    },
    onClickAddUriRule() {
      this.channel.uriRules.push({ name: '', value: '' });
    },
    onClickRemoveUriRule(index) {
      this.channel.uriRules.splice(index, 1);
    },
    onUpdateStream(newStream, index) {
      if (index === 0 || index) {
        this.channel.streams[index] = newStream;
      } else {
        this.channel.streams.push(newStream);
      }
    },
    onDeleteStream(index) {
      this.streams.splice(index, 1);
    },
    async onCreateProject() {
      const payload = {
        name: this.channel.name,
        archiveAgeInMinutes: this.channel.archiveAgeInMinutes * 1440,
        streams: this.channel.streams,
        keyRequestSettings: {
          headers: [
            ...this.channel.headers,
          ],
          uriRewriteRules: [
            ...this.channel.uriRules,
          ],
        },
        teamNamespace: this.channel.teamName,
      };
      try {
        await createChannel(payload);
        this.$router.push(`/${this.teamName}/capture`);
      } catch (error) {
        this.error = error.response.data;
        if (error.response.status === 422) {
          this.error.system = this.$t('validation:fillCorrectInformation');
        }
      }
    },
    onToggleOptionalSection() {
      this.showOptionalSection = !this.showOptionalSection;
    },
    validateRequiredField(fieldName) {
      if (!this.channel[fieldName]) {
        this.$set(this.error, fieldName, this.$t('validation:fillInformation'));
      } else {
        this.$set(this.error, fieldName, '');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/scss/themes/helper.scss';
@import '~@/assets/scss/variables.scss';

.page-header {
  padding-top: $spacer * 1.5;
  padding-bottom: $spacer;
  background-color: $dark-secondary;

  .page-subtitle {
    color: $body-color;
  }
  /* align-items: center; */
}

.general-information-section {
  margin-top: $spacer * 2.5;
  .section-title {
    font-weight: bold;
    display: flex;
    .chevron-icon {
      margin-left: auto;
      cursor: pointer;
    }
    .rotate {
      transform: rotate(180deg);
      transition: ease-in-out 0.2s;
    }
  }
}

.optional-section {
  margin-top: $spacer * 2.5;
  padding-top: $spacer * 2.5;
  border-top: 0.25px solid $dark-600;

  .optional-element-section {
    margin-top: $spacer * 1.5;
    .optional-element {
      display: flex;
      align-items: center;
      position: relative;

      .form-control {
        border-radius: 0;
      }

      .short-form {
        border-right: 0.25px solid $dark-600;
      }

      .close-button-container {
        position: absolute;
        display: flex;
        align-items: center;
        color: $gray-400;
        top: 0;
        bottom: 0;
        right: 0;
        padding: 0 $spacer;
        cursor: pointer;
        border-left: 0.25px solid $dark-600;
        background-color: $dark-700;
      }
    }
  }
}

.form-control {
  border: 1px solid $dark-500;
  background: $dark-secondary;
  color: #FFF;
}
/* .is-invalid {
  border-color: #dc3545;
} */

.form-group {
  margin-top: $spacer;
}

.form-title {
  font-size: $font-size-base;
}

.add-button {
  width: 100%;
  margin-top: $spacer;
}

.short-form {
  width: 200px;
}

.save-button-container {
  margin-top: $spacer * 4;
  margin-bottom: $spacer * 2;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.empty-container {
  width: 100%;
  height: 300px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-flow: column;
  .icon {
    font-size: 72px;
    margin-bottom: $spacer * 2;
  }
}

</style>
